import React, { useState, useEffect } from 'react';
import { FileUpload } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { uploadPdf, getClients } from '../api';
import { useNotification } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/user';
import { Client } from '../types/client';

interface PdfUploadProps {
  onUpload?: (file: File) => Promise<void>;
}

interface PdfJob {
  jobId: string;
  status: string;
}

const PdfUpload: React.FC<PdfUploadProps> = ({ onUpload }) => {
  const { addNotification } = useNotification();
  const [isUploading, setIsUploading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const isCustomerUser = user?.role === UserRole.Customer;

  // For customer users, we'll use their clientId directly from the user object
  const customerClientId = isCustomerUser ? user?.clientId : null;

  useEffect(() => {
    // Only load clients for admin/manager users
    if (!isCustomerUser) {
      const loadClients = async () => {
        try {
          const clientsData = await getClients();
          setClients(clientsData);
        } catch (error) {
          console.error('Error loading clients:', error);
          addNotification('error', 'Failed to load clients');
        }
      };
      loadClients();
    }
  }, [isCustomerUser, addNotification]);

  const handleUpload = async (event: any) => {
    console.log('Upload started with files:', event.files);
    console.log('Current user:', user);
    console.log('Customer client ID:', customerClientId);
    console.log('Selected client ID:', selectedClientId);

    const files = event.files;
    
    // Use customer's clientId if they're a customer user, otherwise use selected client ID
    const clientId = isCustomerUser ? customerClientId : selectedClientId;
    
    if (!clientId) {
      console.error('No client ID available for upload');
      if (!isCustomerUser) {
        addNotification('error', 'Please select a client first');
      }
      return;
    }

    setIsUploading(true);
    
    try {
      console.log('Uploading files for client:', clientId);
      const result = await uploadPdf(files, clientId);
      console.log('Upload result:', result);
      
      // Show success message for all files
      addNotification('success', `${files.length} file(s) have been queued for processing`);
      
      // Log the job IDs for reference
      result.jobs.forEach((job: PdfJob, index: number) => {
        console.log(`PDF Processing Job ID for ${files[index].name}:`, job.jobId);
      });

      // Call onUpload if provided
      if (onUpload) {
        for (const file of files) {
          await onUpload(file);
        }
      }
      
      // Show additional info about async processing
      addNotification('info', 'The PDFs are being processed. Purchase orders will be created automatically when processing is complete.');
      
      // Navigate to purchase orders list where the new POs will appear
      navigate('/purchase-orders');
      
    } catch (error) {
      console.error('Failed to upload PDFs:', error);
      addNotification('error', 'Failed to upload PDFs. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Find the current client for display
  const currentClient = isCustomerUser 
    ? clients.find(c => c.id === customerClientId)
    : clients.find(c => c.id === selectedClientId);

  return (
    <div className="pdf-upload-container">
      <div className="field mb-4">
        <label className="block text-lg mb-2">Client</label>
        {isCustomerUser ? (
          // For customer users, show read-only client name or ID
          <div className="p-inputtext p-component w-full bg-gray-100">
            {currentClient?.name || `Client ID: ${customerClientId}` || 'Loading...'}
          </div>
        ) : (
          // For admin/manager users, show client dropdown
          <select
            className="p-inputtext p-component w-full"
            value={selectedClientId || ''}
            onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Select a client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <FileUpload
        mode="basic"
        name="file"
        multiple
        accept="application/pdf"
        maxFileSize={5000000}
        customUpload
        uploadHandler={handleUpload}
        chooseLabel="Select PDFs"
        uploadLabel={isUploading ? 'Uploading...' : 'Upload PDFs'}
        disabled={isUploading || (!isCustomerUser && !selectedClientId)}
        auto
      />
      {isUploading && (
        <div className="upload-progress mt-2 flex items-center">
          <ProgressSpinner 
            style={{ width: '30px', height: '30px' }} 
            strokeWidth="4" 
            animationDuration=".5s"
          />
          <span className="ml-2">Uploading PDFs...</span>
        </div>
      )}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium mb-2">How PDF Processing Works</h3>
        <ol className="list-decimal ml-4 space-y-2">
          <li>Upload your PDF purchase orders (you can select multiple files)</li>
          <li>The system will queue them for processing</li>
          <li>Our AI will extract all relevant information</li>
          <li>Purchase orders will be created automatically</li>
          <li>You'll find the new purchase orders in the list</li>
        </ol>
        <p className="mt-3 text-sm text-gray-600">
          Note: Processing happens in the background, so you can continue working while waiting.
          The purchase orders will appear in the list when ready.
        </p>
      </div>
    </div>
  );
};

export default PdfUpload;
